import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, styled } from '@mui/material';
import { LiqPrefLedgerConfig, LiqPrefLedgerItem } from 'api';
import {
  liqPrefLedgerInfluencedCellAliases,
  LIQUIDATION_PREFERENCE_LEDGER_ALIAS,
  LIQUIDATION_PREFERENCE_LEDGER_ROW_NUMBER,
} from 'common/constants/cap-table/capTable';
import { LedgerDialog } from 'components/Dialogs';
import { useFeatureSpreadsheet } from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { GridSkeleton } from 'components/Grid';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { useTableValidation } from 'services/hooks';
import { getStringValue } from 'utilities';
import { BASIS_3_VALUE, ISSUE_PRICE_VALUE } from './liq-pref-ledger-table-config/constants';
import useLiquidationPreferenceLedger from './liq-pref-ledger-table-config/useLiquidationPreferenceLedger';
import LiquidationPreferenceLedgerProps from './LiquidationPreferenceLedger.props';
import getLedgerInfluencedCells from '../../utilities/getLedgerInfluencedCells';
import { calculateCellChanges } from '../../utilities/variablePropertiesUtils';

enum LiquidationPreferenceLedgerType {
  GREATER_OF = 'GREATER_OF',
  LESSER_OF = 'LESSER_OF',
  BY_EXIT_DATE = 'BY_EXIT_DATE',
}

// Sentinel value for dividend threshold type to indicate null because
// MUI Select cannot reliably use an empty string as a MenuItem value.
const DIVIDEND_THRESHOLD_NONE_VALUE = 'NONE';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.grey[400],
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputLabel-root': {
    backgroundColor: theme.palette.background.paper,
    padding: '0 4px',
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

const LiquidationPreferenceLedgerDialog = ({
  cell,
  isDisabled,
  closeDialog,
  onInit,
  tableData,
  id = 'liquidation-preference-ledger',
  ...rest
}: LiquidationPreferenceLedgerProps) => {
  const LiquidationPreferenceLedgerTypeOptions = useRef([
    { value: LiquidationPreferenceLedgerType.GREATER_OF, text: 'Greater Of' },
    { value: LiquidationPreferenceLedgerType.LESSER_OF, text: 'Lesser Of' },
    { value: LiquidationPreferenceLedgerType.BY_EXIT_DATE, text: 'By Exit Date' },
  ]);
  const DividendThresholdTypeOptions = useRef([
    { value: DIVIDEND_THRESHOLD_NONE_VALUE, text: 'None' },
    { value: LiquidationPreferenceLedgerType.GREATER_OF, text: 'Greater Of' },
    { value: LiquidationPreferenceLedgerType.LESSER_OF, text: 'Lesser Of' },
  ]);
  const { onCellsChanged: capTableOnCellsChanged, cells: capCells } = useFeatureSpreadsheet();
  const { columnLegend } = cell;
  const existingLiqPrefLedger = cell.sheet?.cells?.[`${columnLegend}${LIQUIDATION_PREFERENCE_LEDGER_ROW_NUMBER}`]
    ?.value as LiqPrefLedgerConfig;

  const cellColumnRef = useMemo(() => cell.columnRef, [cell.columnRef]);
  const startingColumns = useMemo(() => tableData?.columns ?? [], [tableData]);
  const currentSecurity = useMemo(
    () => startingColumns.find(column => column.columnRef === cellColumnRef),
    [startingColumns, cellColumnRef]
  );
  const defaultLedger = {
    items: [
      { name: 'Scenario 1', multiple_of: ISSUE_PRICE_VALUE, dividend_yearfrac_basis: BASIS_3_VALUE },
      { name: 'Scenario 2', multiple_of: ISSUE_PRICE_VALUE, dividend_yearfrac_basis: BASIS_3_VALUE },
    ] as LiqPrefLedgerItem[],
    threshold_type: LiquidationPreferenceLedgerType.GREATER_OF,
  };

  const [ledgerData, setLedgerData] = useState<LiqPrefLedgerConfig>(
    existingLiqPrefLedger?.threshold_type ? existingLiqPrefLedger : defaultLedger
  );

  const isUsingByExitDate = ledgerData.threshold_type === LiquidationPreferenceLedgerType.BY_EXIT_DATE;

  const { spreadsheet } = useLiquidationPreferenceLedger({
    ledgerData: ledgerData.items!,
    isUsingByExitDate,
    isDisabled,
    currentSecurity,
  });
  const spreadsheets = useMemo(() => [spreadsheet], [spreadsheet]);
  const { validateCells } = useTableValidation();

  const { onChange: onCellsChanged, cells, workbook, workbookContextValue, data } = useWorkbook(spreadsheets);

  useEffect(() => {
    onInit?.();
  }, [onInit]);

  const findLedgerCell = useCallback(
    (allCells: ICell[]) =>
      allCells.find(
        (tCell: ICell) => tCell.alias === LIQUIDATION_PREFERENCE_LEDGER_ALIAS && tCell.columnRef === cell.columnRef
      ),
    [cell.columnRef]
  );

  const handleOnSave = useCallback(() => {
    const liqPrefLedgerValue: LiqPrefLedgerItem[] = spreadsheet?.reverseParse();
    const allCells = Object.values(cell.sheet?.cells || {});
    // we only care about the ledger cells for validation
    const { areCellsValid: areLedgerCellsValid } = validateCells({
      cellsToValidate: Object.values(spreadsheet?.cells),
      customValidations: spreadsheet?.customValidations,
      parsedColumns: spreadsheet?.cells,
      useOriginalCell: true,
      isSaving: true,
      tableData: {
        currentSecurity,
        isUsingByExitDate: ledgerData.threshold_type === LiquidationPreferenceLedgerType.BY_EXIT_DATE,
      },
    });

    if (!areLedgerCellsValid) {
      spreadsheet?.updateAllCellsDisplayConfig();
      return;
    }
    const cellWithLiqPrefLedgerAlias = findLedgerCell(allCells);
    const ledgerInfluencedCells = getLedgerInfluencedCells(
      liqPrefLedgerInfluencedCellAliases,
      capCells,
      getStringValue(columnLegend)
    );
    const cellChanges = calculateCellChanges(ledgerInfluencedCells, liqPrefLedgerValue);

    capTableOnCellsChanged([
      ...cellChanges,
      {
        cell: cellWithLiqPrefLedgerAlias,
        value: {
          threshold_type: ledgerData.threshold_type,
          dividend_threshold_type: ledgerData.dividend_threshold_type ?? null,
          items: liqPrefLedgerValue,
          id: ledgerData.id,
        } as LiqPrefLedgerConfig,
      },
    ]);

    closeDialog();
  }, [
    capCells,
    cell,
    spreadsheet,
    ledgerData,
    findLedgerCell,
    capTableOnCellsChanged,
    closeDialog,
    validateCells,
    columnLegend,
    currentSecurity,
  ]);
  const handleAddLedgerItem = useCallback(() => {
    const currentItems = spreadsheet?.reverseParse();

    setLedgerData(prev => ({
      ...prev,
      items: prev.items
        ? ([
          ...currentItems,
          {
            name: `Scenario ${prev.items.length + 1}`,
            dividend_yearfrac_basis: BASIS_3_VALUE,
            multiple_of: ISSUE_PRICE_VALUE,
          },
        ] as LiqPrefLedgerItem[])
        : ([{}] as LiqPrefLedgerItem[]),
    }));
  }, [spreadsheet]);

  const handleThresholdTypeChange = (event: SelectChangeEvent<unknown>) => {
    setLedgerData(prev => ({
      ...prev,
      threshold_type: event.target.value as string,
    }));
  };

  const handleDividendThresholdTypeChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValue = event.target.value as string;

    setLedgerData(prev => ({
      ...prev,
      dividend_threshold_type:
        selectedValue === DIVIDEND_THRESHOLD_NONE_VALUE
          ? null
          : (selectedValue as LiqPrefLedgerConfig['dividend_threshold_type']),
    }));
  };

  const handleDeleteColumn = useCallback(
    (columnIndex: number) => {
      const ledgerItems = spreadsheet?.reverseParse();
      ledgerItems?.splice(columnIndex, 1);
      setLedgerData(prev => ({
        ...prev,
        items: [...ledgerItems],
      }));
    },
    [spreadsheet]
  );

  if (!spreadsheet) {
    return <GridSkeleton />;
  }

  return (
    <LedgerDialog {...rest} id={id} onClose={closeDialog} onSave={handleOnSave}>
      <Box display="flex" gap={2}>
        <StyledFormControl fullWidth>
          <InputLabel id="threshold-type-label">Threshold Type</InputLabel>
          <Select
            labelId="threshold-type-label"
            value={ledgerData.threshold_type}
            onChange={handleThresholdTypeChange}
            variant="outlined">
            {LiquidationPreferenceLedgerTypeOptions.current.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.text}
              </MenuItem>
            ))}
          </Select>
        </StyledFormControl>
        {isUsingByExitDate && (
          <StyledFormControl fullWidth>
            <InputLabel id="dividend-threshold-type-label">Dividend Threshold</InputLabel>
            <Select
              labelId="dividend-threshold-type-label"
              id="dividend-threshold-type-select"
              data-testid="dividend-threshold-type-select"
              value={ledgerData.dividend_threshold_type ?? DIVIDEND_THRESHOLD_NONE_VALUE}
              onChange={handleDividendThresholdTypeChange}
              variant="outlined">
              {DividendThresholdTypeOptions.current.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.text}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        )}
      </Box>
      <WorkbookContext.Provider value={workbookContextValue}>
        <ScalarSpreadsheet
          {...spreadsheet}
          cells={cells}
          onChange={onCellsChanged}
          workbook={workbook}
          sheet={spreadsheet}
          deleteColumn={handleDeleteColumn}
          isLedgerTable
          data={data}
        />
      </WorkbookContext.Provider>
      <Button onClick={handleAddLedgerItem}>Add</Button>
    </LedgerDialog>
  );
};

export default LiquidationPreferenceLedgerDialog;
